// ============================================================================
// history-search.ts — Project History keyword + job-type/category search
// ----------------------------------------------------------------------------
// Powers AllQuotesPage's job-type category chips ("search by job type", Peter
// 2026-07-01): typing "window", "scaffold", "balcony", "flood test", "gutter
// replacement" finds the jobs that had that work in their scope objective /
// scope wording — not just address/title. All fields are already on the
// projects[] summary prop (form_data + projected scope + project_type), so
// this is pure client-side, no fetch. Extracted into a service so it is
// unit-testable and reusable, and so the category set is a single expandable
// source of truth.
//
// matchesHistorySearch + buildProjectHaystack are consumed by AllQuotesPage's
// free-text Project History search (their original home, HistoryModal, was
// deleted as unreachable). Kept exported + unit-tested (history-modal-search.test.ts).
// ============================================================================

import { formatQmQuoteNoDisplay } from './qm-quote-no.js';
import { formatQuoteNumberFileSegment, formatQuoteNumberLabel } from './quote-jobs/quote-number';
import { crmLinkedFirmName } from '../components/crm/client-record-view-model';

// Haystacks are memoized per project OBJECT (WeakMap identity). Safe because
// summary rows are rebuilt as fresh objects on every load/save hydration, and
// the page-level optimistic overrides (status, allocated quote #) live in
// separate state, never mutated onto the row. If a haystack field is ever
// edited in place, the row must be replaced for search to see the change.
const HAYSTACK_CACHE = new WeakMap<object, string>();
const CLASSIFY_CACHE = new WeakMap<object, string>();

/**
 * Build the lowercase search haystack for one Project History summary row.
 * This is THE searchable-field list for Project History — HistoryModal and
 * AllQuotesPage both read it (AllQuotesPage's inline copy was deleted
 * 2026-07-03; it had drifted to a superset). The QM Quote # is indexed in
 * both its raw form ("260522") and its display form ("RBB260522") so the
 * operator can paste either from a document or type what the table shows.
 *
 * It is the job-type haystack below plus the Job fields, which are for
 * FINDING a quote and never for classifying it.
 */
export function buildProjectHaystack(p: any): string {
    if (!p || typeof p !== 'object') return '';
    const cached = HAYSTACK_CACHE.get(p);
    if (cached !== undefined) return cached;
    const base = buildClassificationHaystack(p);
    // Jobs (docs/job/GOAL.md A5, A7). The Quote # is indexed as the SAME label
    // the Run Sheet and the quote print ("RBB260905 Rev 1"), so a number copied
    // off a PDF finds its row, and "rev 1" finds every Rev 1. Another builder's
    // number on an imported quote is indexed raw as well, and the Job title lets
    // one search find every quote of a split job.
    //
    // Kept OUT of the job-type haystack on purpose. A Job title is shared by
    // every quote in the Job, and a builder's number can be any word, so
    // letting them classify would move a Roof quote into Balcony because its
    // Job is called "... Balcony and roof", and would shift the Analysis trade
    // mix and the category chip counts, which A17 says stay as they were.
    //
    // The Quote # is indexed a second time as the PDF file name prints it
    // ("RBB260905-Rev1", "Q-4471-Rev2", another builder's number made
    // file-safe). An operator pastes that token straight off an attachment:
    // the query "260905-Rev1" splits into the revision term "rev1" and the
    // token "260905-", and only this form holds "260905-".
    const rawQmQuoteNo = p.qmQuoteNo || p.qm_quote_no;
    const sourceQuoteRef = p.sourceQuoteRef || p.source_quote_ref;
    const quoteNumberInput = {
        qmQuoteNo: rawQmQuoteNo,
        quoteRev: Number(p.quoteRev ?? p.quote_rev ?? 0),
        sourceQuoteRef,
    };
    const quoteNoLabel = formatQuoteNumberLabel(quoteNumberInput);
    const quoteNoFile = formatQuoteNumberFileSegment(quoteNumberInput);
    const jobFields = [
        quoteNoLabel,
        quoteNoFile !== quoteNoLabel ? quoteNoFile : '',
        sourceQuoteRef,
        p.quoteJobTitle, p.quote_job_title,
    ].filter(Boolean).join(' | ').toLowerCase();
    // The firm the job is for (SPEC 2026-10-08 A2.3): the linked client's
    // name and the requester company, so a search for "Strata Partners" finds
    // a strata job whose client field prints "The Owners of Strata Plan". Kept
    // out of the job-type haystack for the same reason as the Job fields: a
    // firm called "... Roofing" must not move a quote into the Roof category.
    // A linked building row is not the firm (crmLinkedFirmName), so it is not
    // indexed as one.
    const fd = p.formData || p.form_data || {};
    const firmFields = [crmLinkedFirmName(p), fd.requesterCompany]
        .filter(value => typeof value === 'string' && value.trim()).join(' | ').toLowerCase();
    const haystack = [base, jobFields, firmFields].filter(Boolean).join(' | ');
    HAYSTACK_CACHE.set(p, haystack);
    return haystack;
}

/**
 * The haystack the job-type categories classify against: every field above
 * except the Job fields. Unchanged by Jobs, so a project classifies exactly as
 * it did before.
 */
function buildClassificationHaystack(p: any): string {
    if (!p || typeof p !== 'object') return '';
    const cached = CLASSIFY_CACHE.get(p);
    if (cached !== undefined) return cached;
    const scopeText = Array.isArray(p.scope)
        ? p.scope.map((s: any) => `${s?.title || ''} ${s?.description || ''} ${s?.description_brief || ''} ${s?.code || ''}`).join(' ')
        : '';
    const fd = p.formData || {};
    const rawQmQuoteNo = p.qmQuoteNo || p.qm_quote_no;
    const isPdf = Boolean(p.pdfGenerated || p.pdf_generated);
    const isSent = Boolean(p.emailSent || p.email_sent);
    const haystack = [
        p.address, p.title, p.quoteNumber,
        fd.address, fd.unitNumber,
        fd.clientName, fd.clientCompany, fd.strataplan,
        rawQmQuoteNo, formatQmQuoteNoDisplay(rawQmQuoteNo),
        p.intakeQuoteRequestNo, p.intake_quote_request_no,
        p.projectType, p.project_type,
        scopeText,
        fd.scopeObjective, fd.scope_objective, fd.jobDescription,
        p.quoteNote, p.quote_note,
        isPdf ? 'pdf pdf-made made' : '',
        isSent ? 'sent quote-sent' : '',
    ].filter(Boolean).join(' | ').toLowerCase();
    CLASSIFY_CACHE.set(p, haystack);
    return haystack;
}

/**
 * A revision is searched as ONE term. Split on whitespace, "rev 1" would ask
 * for "rev" anywhere (a substring of "prevent", "review", "revised", all
 * common in remediation scopes) and "1" anywhere (in almost every QR, address
 * or quote number), so token-AND would match most of the book instead of the
 * Rev 1 quotes. So "rev 1", "Rev 1" and "rev1" become one term that needs
 * "rev" at the start of a word, then the number, then no further digit:
 * "RBB260905 Rev 1" and a hand-typed "Rev 1.0" title match, "prevent" and
 * "Rev 12" do not. "rev" typed on its own stays a plain substring, so
 * incremental typing still narrows as it did.
 *
 * The term matches every spelling a hand-marked legacy title uses for a
 * revision, the same set parseRevisionLabel (quote-jobs/quote-number.ts) reads:
 * "Rev 1", "rev1", "Rev. 1", "Rev-1", "Rev: 1", "Revised 1.0", "Revision 1",
 * "Revision No. 1". Those rows have quote_rev 0 (migration 120 does not
 * backfill), so the title is the only place their revision lives, and the
 * token search before v6.0.56 found them. "review", "preview" and "revenue"
 * still do not match: after "rev" only "ision" or "ised" may follow before the
 * number.
 */
const REVISION_QUERY = /(?<![a-z0-9])rev\s*(\d{1,2})(?!\d)/g;

/** Hyphen, en dash and em dash, as they appear between "Rev" and its number. */
const REVISION_DASHES = '\\-\\u2013\\u2014';

interface SearchTerms {
    /** Revision terms, each a pattern the haystack must match. */
    revisions: RegExp[];
    /** Every other whitespace-separated token, as typed (lowercase). */
    tokens: string[];
}

function splitSearchTerms(q: string): SearchTerms {
    const revisions: RegExp[] = [];
    const rest = q.replace(REVISION_QUERY, (_match, n: string) => {
        revisions.push(new RegExp(
            `(?<![a-z0-9])rev(?:ision|ised)?(?:\\s*(?:no|number)\\.?)?[\\s:#.${REVISION_DASHES}]*0*${Number(n)}(?!\\d)`,
        ));
        return ' ';
    });
    return { revisions, tokens: rest.split(/\s+/).filter(Boolean) };
}

/**
 * Match a project against a free-text query. Token-AND: every whitespace-
 * separated term must appear somewhere in the haystack, so "balcony membrane"
 * matches a job whose objective says balcony and whose scope says membrane, and
 * "gutter replacement" matches a scope line reading gutter replacement.
 * Free text stays plain substring (unlike the category keywords below) so
 * incremental typing works: "scaff" already narrows to scaffold jobs. The one
 * exception is a revision ("rev 1"), matched as a single term (see
 * splitSearchTerms), the same way the Run Sheet matches it.
 * scopeSearchOn=false reverts to the legacy address/title/quote# substring.
 */
export function matchesHistorySearch(p: any, rawQuery: string, scopeSearchOn: boolean): boolean {
    const q = String(rawQuery || '').trim().toLowerCase();
    if (!q) return true;
    if (!scopeSearchOn) {
        const legacy = `${p?.address || ''} ${p?.title || ''} ${p?.quoteNumber || ''}`.toLowerCase();
        return legacy.includes(q);
    }
    const haystack = buildProjectHaystack(p);
    const { revisions, tokens } = splitSearchTerms(q);
    return revisions.every(rx => rx.test(haystack))
        && tokens.every(term => haystack.includes(term));
}

export interface HistoryCategory {
    id: string;
    label: string;
    /**
     * Lowercase keywords, matched at a word START with an open right edge:
     * 'tile' hits "tile/tiles/tiled" but not "cantilever"; 'waterproof' still
     * hits "waterproofing"; 'ewp' no longer fires inside "Newport". Multi-word
     * phrases tolerate spaces or hyphens ("flood test" ≡ "flood-test"), so
     * prefixed forms that lose the boundary (retile, reroof) need their own
     * keyword. Any hit classifies the project into this category.
     */
    keywords: string[];
}

// Curated job-type categories for the quick-filter chips. This is a small,
// scannable set (not the full 52-slug meta-learning taxonomy — that is for the
// learning system). Expandable: add a row to grow the chip set + classifier.
// Ordered by how often RJ Bird quotes these.
export const HISTORY_CATEGORIES: HistoryCategory[] = [
    { id: 'waterproofing', label: 'Waterproofing', keywords: ['waterproof', 'membrane', 'tanking', 'sarking'] },
    { id: 'balcony', label: 'Balcony', keywords: ['balcony', 'balconies', 'balustrade'] },
    { id: 'concrete', label: 'Concrete repair', keywords: ['concrete', 'spall', 'spalling', 'remediation', 'render', 'crack injection', 'epoxy'] },
    // 2026-07-03: measured against the live 123-project corpus — 15 rows
    // (12%) classified into ZERO categories. Ceiling was the biggest gap
    // (6 uncovered rows; 39 rows total once added — second only to
    // concrete), fencing + bathroom cover most of the rest. Keyword hit
    // counts over the full corpus: ceiling 34, plasterboard 13, cornice 5;
    // bathroom 9, shower 3; fence 7, fencing 4, paling(s) 4. 'gyprock' and
    // 'ensuite' have no hits today but are standard AU synonyms an operator
    // will eventually type. Open right edge means 'paling' already covers
    // "palings", 'fence' does NOT cover "fencing" (hence both).
    { id: 'ceiling', label: 'Ceiling', keywords: ['ceiling', 'plasterboard', 'gyprock', 'cornice'] },
    { id: 'gutter', label: 'Gutter', keywords: ['gutter', 'downpipe', 'box gutter', 'stormwater'] },
    { id: 'roofing', label: 'Roof', keywords: ['roof', 'roofing', 'reroof', 'flashing', 'ridge cap', 'tile roof', 'metal roof'] },
    { id: 'window', label: 'Window', keywords: ['window', 'glazing', 'glazed'] },
    { id: 'scaffold', label: 'Scaffold', keywords: ['scaffold', 'rope access', 'abseil', 'ewp', 'access equipment'] },
    { id: 'flood-test', label: 'Flood test', keywords: ['flood test', 'flood-test', 'water test'] },
    { id: 'painting', label: 'Painting', keywords: ['paint', 'painting', 'repaint', 'coating'] },
    { id: 'tiling', label: 'Tiling', keywords: ['tile', 'tiling', 'retile', 'grout', 'regrout'] },
    { id: 'bathroom', label: 'Bathroom', keywords: ['bathroom', 'ensuite', 'shower'] },
    { id: 'fencing', label: 'Fencing', keywords: ['fence', 'fencing', 'paling'] },
];

/**
 * Compile one keyword to its word-start regex: `\b` on the left, open right
 * edge (prefix match), interior separators normalised to space-or-hyphen runs.
 */
function compileKeyword(keyword: string): RegExp {
    const escaped = keyword.trim().toLowerCase()
        .split(/[\s-]+/)
        .map(word => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
        .join('[\\s-]+');
    return new RegExp(`\\b${escaped}`);
}

interface CompiledCategory extends HistoryCategory {
    patterns: RegExp[];
}

const COMPILED_CATEGORIES: CompiledCategory[] = HISTORY_CATEGORIES.map(c => ({
    ...c,
    patterns: c.keywords.map(compileKeyword),
}));

const COMPILED_BY_ID: Record<string, CompiledCategory> = Object.fromEntries(
    COMPILED_CATEGORIES.map(c => [c.id, c]),
);

function categoryHits(cat: CompiledCategory, haystack: string): boolean {
    return cat.patterns.some(rx => rx.test(haystack));
}

/** Classify a project into 0..N category ids by scanning its haystack. */
export function classifyProjectCategories(p: any): string[] {
    const haystack = buildClassificationHaystack(p);
    if (!haystack) return [];
    return COMPILED_CATEGORIES
        .filter(c => categoryHits(c, haystack))
        .map(c => c.id);
}

/** Human label for a category id (falls back to the id). */
export function categoryLabel(id: string): string {
    return COMPILED_BY_ID[id]?.label || id;
}

/**
 * Run Sheet (dashboard) search — token-AND with job-type synonym expansion.
 *
 * Each whitespace token must be satisfied by EITHER:
 *   (a) a plain substring hit in the project haystack (title, address, client,
 *       SP, quote numbers raw + display, the Quote # label with its Rev N,
 *       another builder's quote number, the Job title, scope wording, job
 *       description), OR
 *   (b) category membership: the token prefix-matches a curated category's
 *       label, id or one of its keywords, and the project classifies into that
 *       category. This is what makes "balcony" find a job whose scope only
 *       says "balustrade", and "concrete rendering" find spalling/render jobs
 *       that never use the literal phrase.
 *
 * A revision ("rev 1", "rev1") is one term, not two, and must match "Rev N" as
 * a word in the haystack (see splitSearchTerms).
 *
 * Built on the same haystack + category set as Project History, so the two
 * search boxes can never drift apart. Pure client-side; the haystack is
 * memoized per row object.
 */
export function matchesRunSheetSearch(p: any, rawQuery: string): boolean {
    const q = String(rawQuery || '').trim().toLowerCase();
    if (!q) return true;
    const haystack = buildProjectHaystack(p);
    if (!haystack) return false;
    let projectCategories: Set<string> | null = null;
    // Bidirectional prefix: "rend" names the concrete category via its
    // 'render' keyword (typed prefix), and so does "rendering" (inflected
    // longer form). One direction alone misses half of real typing.
    const namesTerm = (term: string, token: string) =>
        term.startsWith(token) || token.startsWith(term);
    const { revisions, tokens } = splitSearchTerms(q);
    if (!revisions.every(rx => rx.test(haystack))) return false;
    return tokens.every(token => {
        if (token === 'unsent' || token === 'not-sent') {
            return !(p?.emailSent || p?.email_sent);
        }
        if (token === 'nopdf' || token === 'no-pdf') {
            return !(p?.pdfGenerated || p?.pdf_generated);
        }
        if (haystack.includes(token)) return true;
        // Synonym leg: which categories does this token name?
        const named = COMPILED_CATEGORIES.filter(c =>
            namesTerm(c.id, token)
            || namesTerm(c.label.toLowerCase(), token)
            || c.keywords.some(k => namesTerm(k, token)),
        );
        if (!named.length) return false;
        if (!projectCategories) projectCategories = new Set(classifyProjectCategories(p));
        return named.some(c => projectCategories!.has(c.id));
    });
}

/**
 * Does a project match ANY of the active category ids? Chips are OR within
 * the job-type facet — selecting Balcony+Gutter shows balcony jobs AND gutter
 * jobs — while the whole facet still AND-composes with the free-text search
 * and the status filter at the call sites. (AND across chips shipped first on
 * 2026-07-01 but two disjoint trades almost always intersect to zero rows;
 * "must have both" is already expressible as free-text token-AND.)
 * Empty activeIds => always true.
 */
export function matchesActiveCategories(p: any, activeIds: string[] | Set<string>): boolean {
    const ids = Array.isArray(activeIds) ? activeIds : Array.from(activeIds || []);
    if (!ids.length) return true;
    const haystack = buildClassificationHaystack(p);
    return ids.some(id => {
        const cat = COMPILED_BY_ID[id];
        return cat ? categoryHits(cat, haystack) : false;
    });
}

/**
 * Per-category match counts for a list of projects — feeds the count badges
 * on the job-type chips. Call it with the list already narrowed by the OTHER
 * filters (free text + status) but NOT by the chips themselves, so each badge
 * reads "how many rows this chip shows on its own" (faceted-search idiom,
 * matching the status summary strip's counts).
 */
export function countCategoryMatches(projects: any[]): Record<string, number> {
    const counts: Record<string, number> = {};
    for (const c of COMPILED_CATEGORIES) counts[c.id] = 0;
    for (const p of projects || []) {
        const haystack = buildClassificationHaystack(p);
        if (!haystack) continue;
        for (const c of COMPILED_CATEGORIES) {
            if (categoryHits(c, haystack)) counts[c.id] = (counts[c.id] ?? 0) + 1;
        }
    }
    return counts;
}
